/* ============================================================================
 * AppearanceProvider — one appearance instance for the whole OS
 * ----------------------------------------------------------------------------
 * The appearance must be a single shared instance: SETTINGS.EXE edits it and
 * the desktop must re-render from the same state. Calling useAppearanceState()
 * directly in two components would create two independent setups, so the state
 * lives here and every consumer reads it through context.
 * ========================================================================= */

import { createContext, useContext, type ReactNode } from 'react';
import {
  useAppearanceState,
  type AppearanceController,
} from '../hooks/useAppearance';

const AppearanceCtx = createContext<AppearanceController | null>(null);

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const controller = useAppearanceState();
  return (
    <AppearanceCtx.Provider value={controller}>{children}</AppearanceCtx.Provider>
  );
}

/** Read the live appearance. Throws if there is no provider — that is a bug. */
export function useAppearance(): AppearanceController {
  const ctx = useContext(AppearanceCtx);
  if (!ctx) {
    throw new Error('useAppearance must be used inside <AppearanceProvider>');
  }
  return ctx;
}
